/**
 * La sesión.
 *
 * Guarda quién entró y su token, y —lo importante— es el ÚNICO lugar que le
 * dice al cliente HTTP cuál es el token. Cualquier pantalla que llame a la
 * API lo manda sin acordarse de nada.
 *
 * El usuario guardado es un retrato del momento del login. `refresh()` va a
 * preguntarle al servidor quién es ahora: es lo que hace que subir una foto
 * actualice el avatar de la barra sin volver a entrar, y lo que descubre que
 * la sesión guardada ya no sirve.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { AuthResponse, AuthUser } from '@tennisup/contracts';

import { api, setAccessToken, setOnUnauthorized, setRenovarSesión } from '../lib/api';
import { readStored, writeStored } from '../lib/storage';

const SESSION_STORAGE_KEY = 'tennisup.session';

/*
  LAS CLAVES VIEJAS, DE CUANDO LA MARCA ERA «RetosCR».

  Al cambiar el nombre cambió también dónde se guarda la sesión, y una clave
  nueva significa que el navegador de quien ya había entrado no encuentra
  nada: se despierta afuera, sin saber por qué. Estas dos constantes existen
  para que eso no pase —se leen una sola vez, al arrancar, y lo que había se
  copia a la clave nueva—.

  SE PUEDEN BORRAR, y ese es el plan: cuando haya pasado tiempo suficiente
  para que todo el mundo haya abierto la aplicación al menos una vez, esto y
  `rescatarClaveVieja` se van sin dejar nada atrás.
*/
const SESSION_KEY_VIEJA = 'retoscr.session';
const ACCOUNTS_KEY_VIEJA = 'retoscr.accounts';

/**
 * Trae lo que estaba guardado con el nombre viejo, si hace falta.
 *
 * NO PISA LO NUEVO: si ya hay algo en la clave nueva, esa manda y la vieja se
 * ignora. Lo contrario haría que una sesión vieja tapara la actual cada vez
 * que se abre la aplicación.
 *
 * Y NO BORRA LA VIEJA: si alguien vuelve a una versión anterior de la
 * aplicación —pasa, con el caché de un service worker— la encuentra donde la
 * dejó. Ocupa unos cientos de bytes.
 */
function rescatarClaveVieja(nueva: string, vieja: string): string | null {
  const actual = readStored(nueva);
  if (actual !== null) return actual;

  const anterior = readStored(vieja);
  if (anterior === null) return null;

  writeStored(nueva, anterior);
  return anterior;
}

/**
 * LAS CUENTAS LIGADAS, incluida la que está puesta.
 *
 * VA APARTE DE `tennisup.session` Y NO EN SU LUGAR. Esa clave la lee el
 * arranque de la aplicación y las pruebas, y cambiarle la forma habría roto
 * todo lo que ya la usa por una función nueva. Acá la lista es la novedad y
 * la sesión activa sigue siendo lo que siempre fue.
 *
 * QUÉ SE GUARDA: el usuario y su token. LA CONTRASEÑA NO, ni cifrada ni
 * codificada: se usa una vez para pedir el token y no se vuelve a necesitar.
 * Guardarla convertiría este dispositivo en la copia de seguridad de la
 * contraseña de cada cuenta ligada.
 */
const ACCOUNTS_STORAGE_KEY = 'tennisup.accounts';

interface StoredSession {
  user: AuthUser;
  accessToken: string;
  /**
   * LA LLAVE PARA RENOVAR ESTA SESIÓN, cuando el servidor la dio.
   *
   * OPCIONAL, y no por descuido: en el teléfono de cualquiera que ya estaba
   * dentro hay una sesión guardada SIN esto, y tiene que seguir andando. Sin
   * llave no se renueva y todo funciona como antes — el 401 cierra la sesión —
   * hasta que la persona vuelva a entrar y le den una.
   *
   * VA POR CUENTA Y NO SUELTA: cada cuenta ligada tiene su propia cadena, así
   * que la llave vive con su sesión. Guardar una sola arriba haría que
   * cambiarse de cuenta renovara la sesión equivocada.
   */
  refreshToken?: string;
}

interface AuthContextValue {
  session: StoredSession | null;
  /** Todas las cuentas ligadas en este teléfono, incluida la activa. */
  accounts: StoredSession[];
  signIn: (response: AuthResponse) => void;
  signOut: () => void;
  /** Suma otra cuenta SIN cambiarse a ella: cambiarse es otro gesto. */
  linkAccount: (response: AuthResponse) => void;
  /** Se cambia a esa cuenta. Si no está ligada, no hace nada. */
  switchTo: (userId: string) => void;
  /** La quita de este teléfono. Ver `unlink` para qué pasa con la activa. */
  unlink: (userId: string) => void;
  /** Reemplaza el usuario guardado, conservando el token. */
  setUser: (user: AuthUser) => void;
  /** Le pregunta al servidor quién es ahora. Silencioso si falla. */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readSession(): StoredSession | null {
  const raw = rescatarClaveVieja(SESSION_STORAGE_KEY, SESSION_KEY_VIEJA);
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as StoredSession).accessToken === 'string'
    ) {
      return parsed as StoredSession;
    }
  } catch {
    // Guardado corrupto: es como no tener sesión.
  }
  return null;
}

/**
 * LA SESIÓN GUARDADA A PARTIR DE LO QUE CONTESTÓ EL SERVIDOR.
 *
 * La llave se copia SOLO SI VINO: con `exactOptionalPropertyTypes`, escribir
 * `refreshToken: undefined` no es lo mismo que no escribirlo, y lo primero
 * dejaría la clave presente y vacía en el JSON guardado.
 */
function sesiónDe(response: AuthResponse): StoredSession {
  const base: StoredSession = { user: response.user, accessToken: response.accessToken };
  return response.refreshToken === undefined
    ? base
    : { ...base, refreshToken: response.refreshToken };
}

function persist(session: StoredSession | null): void {
  writeStored(SESSION_STORAGE_KEY, session === null ? '' : JSON.stringify(session));
  setAccessToken(session?.accessToken ?? null);
}

function readAccounts(): StoredSession[] {
  const raw = rescatarClaveVieja(ACCOUNTS_STORAGE_KEY, ACCOUNTS_KEY_VIEJA);
  if (raw === null || raw === '') return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Se filtra fila por fila: un guardado a medias —una actualización
    // interrumpida, otra pestaña escribiendo— no puede tumbar el arranque de
    // toda la aplicación.
    return parsed.filter(
      (fila): fila is StoredSession =>
        typeof fila === 'object' &&
        fila !== null &&
        typeof (fila as StoredSession).accessToken === 'string' &&
        typeof (fila as StoredSession).user?.id === 'string',
    );
  } catch {
    return [];
  }
}

function persistAccounts(accounts: readonly StoredSession[]): void {
  writeStored(ACCOUNTS_STORAGE_KEY, accounts.length === 0 ? '' : JSON.stringify(accounts));
}

/** La misma lista con esa cuenta puesta al día, o agregada si no estaba. */
function conLaCuenta(
  accounts: readonly StoredSession[],
  session: StoredSession,
): StoredSession[] {
  const sinEsa = accounts.filter((a) => a.user.id !== session.user.id);
  return [...sinEsa, session];
}

/**
 * UNA SALIDA QUE NO LLEGÓ AL SERVIDOR.
 *
 * Cerrar sesión borra lo local al instante, pero la cookie de sesión es
 * `HttpOnly` y solo la puede borrar el servidor. Si esa llamada falla —sin
 * red, en un ascensor, con el avión encendido— la cookie QUEDA, y el servidor
 * la acepta sola: quien creyó que salía volvería a entrar al recargar.
 *
 * Se anota el pendiente y se reintenta al arrancar, que es la próxima vez que
 * hay red. La marca vive en `localStorage` a propósito: es lo único que
 * sobrevive a cerrar la app, que es justo cuando hace falta.
 */
const SALIDA_PENDIENTE = 'tennisup.salida-pendiente';

function marcarSalidaPendiente(): void {
  writeStored(SALIDA_PENDIENTE, '1');
}

/**
 * REINTENTA LA SALIDA QUE QUEDÓ COLGADA, si hay alguna.
 *
 * Se llama al arrancar y ANTES de mirar la sesión guardada: si hay salida
 * pendiente es porque no quedó sesión local, así que no hay nada que pisar.
 */
function reintentarSalidaPendiente(): void {
  if (readStored(SALIDA_PENDIENTE) !== '1') return;
  void api
    .logout()
    .then(() => writeStored(SALIDA_PENDIENTE, ''))
    // Sigue sin red: se deja anotado para el próximo arranque.
    .catch(() => undefined);
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [session, setSession] = useState<StoredSession | null>(() => {
    const stored = readSession();
    // Al ARRANCAR, no solo al entrar: si la persona recargó la página, el
    // token ya estaba guardado y el cliente HTTP no lo sabe todavía.
    setAccessToken(stored?.accessToken ?? null);
    return stored;
  });

  /**
   * LA SESIÓN QUE YA ESTABA CUENTA COMO CUENTA LIGADA.
   *
   * Quien entró ANTES de que existiera esta pantalla tiene `tennisup.session`
   * y no tiene lista: sin esto, abría «Más» y veía una lista vacía con su
   * propia sesión puesta —«¿entonces con cuál estoy?»— y la única forma de
   * aparecer ahí era cerrar sesión y volver a entrar.
   *
   * No se guarda al arrancar, se guarda cuando algo cambie: escribir en el
   * almacenamiento durante el primer dibujado es la clase de efecto que
   * revienta donde el almacenamiento está bloqueado.
   */
  const [accounts, setAccounts] = useState<StoredSession[]>(() => {
    const guardadas = readAccounts();
    const activa = readSession();
    if (activa === null) return guardadas;
    return guardadas.some((a) => a.user.id === activa.user.id)
      ? guardadas
      : conLaCuenta(guardadas, activa);
  });

  const signIn = useCallback((response: AuthResponse) => {
    const next: StoredSession = sesiónDe(response);
    setSession(next);
    persist(next);
    // Entrar liga esa cuenta: quien entra a mano en este teléfono es una
    // cuenta de este teléfono, y así la primera aparece en la lista sin que
    // haya que ligarla aparte.
    setAccounts((actuales) => {
      const próximas = conLaCuenta(actuales, next);
      persistAccounts(próximas);
      return próximas;
    });
  }, []);

  /**
   * CERRAR SESIÓN SE LLEVA TODAS LAS CUENTAS.
   *
   * En un teléfono prestado, «cerrar sesión» tiene que significar «no queda
   * nada mío acá». Si dejara ligadas las otras, quien lo agarre después entra
   * a cualquiera de ellas con un toque, sin contraseña — que es exactamente
   * lo contrario de lo que alguien cree que está haciendo.
   */
  const signOut = useCallback(() => {
    /*
     * Y LE AVISA AL SERVIDOR PARA QUE BORRE LA COOKIE.
     *
     * La cookie de sesión es `HttpOnly`: esta pantalla no puede borrarla. Sin
     * este aviso, limpiar `localStorage` no alcanza —el navegador sigue
     * mandando la cookie— y quien cierra sesión volvería a entrar solo al
     * recargar.
     *
     * NO SE ESPERA: salir tiene que sentirse inmediato, y dejar a alguien
     * mirando un spinner porque se cayó la red sería lo contrario de lo que
     * pidió. Lo local se limpia ya.
     *
     * SI LA LLAMADA FALLA, LA COOKIE QUEDA, y eso NO es inocuo: el servidor
     * acepta la cookie sola, así que quien salió sin red volvería a entrar al
     * recargar. Se traga el error —no hay nada mejor que hacer en ese
     * momento— pero se deja anotado para reintentarlo al arrancar, que es la
     * próxima vez que hay red y que la cookie todavía importa.
     */
    // LA LLAVE VIAJA para que la sesión muera también del lado del servidor:
    // borrar la cookie deja de alcanzar cuando hay una llave que sigue
    // sirviendo para pedir sesiones nuevas.
    void api.logout(readSession()?.refreshToken).catch(() => marcarSalidaPendiente());

    setSession(null);
    persist(null);
    setAccounts([]);
    persistAccounts([]);
  }, []);

  const linkAccount = useCallback((response: AuthResponse) => {
    // NO SE CAMBIA A ELLA. Ligar y cambiarse son dos gestos distintos: quien
    // liga la cuenta de su hija no quiere quedar dentro de la cuenta de su
    // hija.
    setAccounts((actuales) => {
      const próximas = conLaCuenta(actuales, sesiónDe(response));
      persistAccounts(próximas);
      return próximas;
    });
  }, []);

  const switchTo = useCallback((userId: string) => {
    setAccounts((actuales) => {
      const cuenta = actuales.find((a) => a.user.id === userId);
      if (cuenta !== undefined) {
        setSession(cuenta);
        persist(cuenta);
      }
      return actuales;
    });
  }, []);

  /**
   * QUITAR UNA CUENTA DE ESTE TELÉFONO.
   *
   * Si es la que está puesta, hay que ir a alguna parte: se pasa a otra
   * ligada si queda alguna, y si no queda ninguna se sale a la pantalla de
   * entrar. Quedarse «adentro» con una cuenta que se acaba de quitar es la
   * forma más rápida de que alguien siga viendo datos que creía haber sacado.
   */
  const unlink = useCallback((userId: string) => {
    setAccounts((actuales) => {
      const quedan = actuales.filter((a) => a.user.id !== userId);
      persistAccounts(quedan);

      setSession((activa) => {
        if (activa === null || activa.user.id !== userId) return activa;
        const siguiente = quedan[0] ?? null;
        persist(siguiente);
        return siguiente;
      });

      return quedan;
    });
  }, []);

  const setUser = useCallback((user: AuthUser) => {
    setSession((current) => {
      if (current === null) return null;
      const next = { ...current, user };
      persist(next);
      // La lista también: si no, cambiar la foto deja la vieja en la fila de
      // esa cuenta hasta que alguien vuelva a entrar.
      setAccounts((actuales) => {
        const próximas = conLaCuenta(actuales, next);
        persistAccounts(próximas);
        return próximas;
      });
      return next;
    });
  }, []);

  /**
   * SI EL SERVIDOR DICE QUE LA SESIÓN NO VALE, SE CIERRA Y SE SALE.
   *
   * Antes el 401 lo mostraba cada pantalla como un error suyo —«tu sesión se
   * venció»— con la barra de arriba todavía diciendo el nombre y la foto, y
   * la lista cargando para siempre. Quien lo veía no tenía cómo saber que la
   * salida era ir a «Más» y cerrar sesión a mano.
   *
   * No hace falta navegar acá: `RequireAuth` manda al login solo, en cuanto
   * la sesión queda en nulo. Una redirección escrita a mano además pelearía
   * con esa, y ganaría la que corra segunda.
   */
  /**
   * UN 401 SACA A LA CUENTA QUE SE VENCIÓ, NO A TODAS.
   *
   * Con `signOut` acá, el token vencido de UNA cuenta desligaba las otras
   * cuatro del teléfono: para volver a entrar con cualquiera había que
   * escribir su contraseña de nuevo. Lo que se venció es una sesión, no el
   * dispositivo. Si queda otra ligada, se pasa a ella; si no, se sale.
   */
  useEffect(() => {
    setOnUnauthorized(() => {
      setSession((activa) => {
        if (activa === null) return null;
        const quedan = readAccounts().filter((a) => a.user.id !== activa.user.id);
        persistAccounts(quedan);
        setAccounts(quedan);
        const siguiente = quedan[0] ?? null;
        persist(siguiente);
        return siguiente;
      });
    });
    return () => setOnUnauthorized(null);
  }, []);

  /**
   * RENOVAR LA SESIÓN SOLA, sin que nadie se entere.
   *
   * Lo llama el cliente HTTP cuando una petición recibe 401: si acá se
   * consigue una sesión nueva, la petición se repite y para quien mira la
   * pantalla no pasó nada. Si no, el 401 sigue su camino y se cierra sesión,
   * que es como funcionaba antes.
   *
   * SE LEE DE `localStorage` Y NO DEL ESTADO DE REACT, a propósito: esta
   * función se registra una vez —el efecto no tiene dependencias— y si mirara
   * `session` se quedaría para siempre con la sesión del primer render. Lo
   * guardado siempre es lo último; el estado de este closure, no.
   */
  useEffect(() => {
    setRenovarSesión(async () => {
      const activa = readSession();
      // SIN LLAVE NO HAY NADA QUE INTENTAR: es una sesión de antes de que esto
      // existiera. Se contesta `false` y el 401 hace lo de siempre.
      if (activa?.refreshToken === undefined) return false;

      try {
        const renovada = await api.refreshSession({ refreshToken: activa.refreshToken });
        const próxima = sesiónDe(renovada);
        setSession(próxima);
        persist(próxima);
        // Y EN LA LISTA DE CUENTAS TAMBIÉN: si solo se actualizara la activa,
        // cambiarse a otra y volver traería la llave vieja —ya canjeada—, que
        // el servidor lee como un robo y contesta matando la cadena.
        setAccounts((actuales) => {
          const próximas = conLaCuenta(actuales, próxima);
          persistAccounts(próximas);
          return próximas;
        });
        return true;
      } catch {
        // Cualquier fallo es «no se pudo renovar»: la llave murió, o no hay
        // red. En los dos casos quien llama decide qué hacer.
        return false;
      }
    });
    return () => setRenovarSesión(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      setUser(await api.me());
    } catch {
      // Silencioso a propósito: un problema de red pasajero no puede sacar a
      // nadie de la aplicación. La sesión vencida sí saca, pero eso lo
      // resuelve el 401 de arriba y no este `catch`.
    }
  }, [setUser]);

  // Una vez, al abrir la app con una sesión guardada: lo que hay en
  // localStorage puede tener días.
  useEffect(() => {
    // Antes que nada: una salida que no llegó al servidor deja la cookie viva.
    reintentarSalidaPendiente();
    if (session !== null) void refresh();
    // Solo al montar: `refresh` es estable y `session` cambiaría en cada
    // actualización, dejando esto en un bucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ session, accounts, signIn, signOut, linkAccount, switchTo, unlink, setUser, refresh }),
    [session, accounts, signIn, signOut, linkAccount, switchTo, unlink, setUser, refresh],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (value === null) throw new Error('useAuth fuera de <AuthProvider>');
  return value;
}
